/**
 * オファー作成ウィザードの状態 / フォームデータの型定義。
 *
 * - ウィザードはスレッド一覧上の「ドラフトスレッド」として並び、選択すると
 *   右カラムにオーバーレイで開く。
 * - ドラフト1件＝1スレッド分の状態を、Redux スライス側で `byThreadKey`
 *   マップとして管理する。本ファイルはその「1スレッド分」の状態の型を定義する。
 */

export type OfferStep = "recipient" | "role" | "form" | "complete";

export type RecipientMode = "follows" | "email";

/**
 * ステップナビゲーションの並び順。
 * - `recipient_first`: ①送信先 / ②ロール（既定）
 * - `role_first`: ①ロール / ②送信先
 *
 * ①が未選択のまま②をクリックしたときに入れ替えが発生する。
 */
export type StepOrder = "recipient_first" | "role_first";

/** 送信先決定ステップで保持する状態 */
export interface OfferRecipientState {
  mode: RecipientMode;
  /** "follows" のときに選択されるユーザー ID */
  userId?: number;
  /** "email" のときに保持される入力値（バリデーション通過済み） */
  email?: string;
  /**
   * "email" のときに任意で入力できる招待相手の表示名。
   * 未登録ユーザーへ招待アカウントを作る際の暫定表示名（`stage_name`）として
   * BE に渡す（登録完了時に本人が上書き可能）。
   */
  username?: string;
}

/** ドラフトスレッド一覧で表示するためのサマリ情報 */
export interface OfferDraftSummary {
  /** 受信先表示名（フォロー中ユーザーの `display_name_jp` / `display_name_en`、メアド経路はメアド） */
  recipientLabel?: string;
  /** 受信先アバターURL（フォロー中ユーザーのみ） */
  recipientAvatarUrl?: string | null;
  /** 受信先のフォールバック名（アバター色用ハッシュ） */
  recipientName?: string;
  /** ロール表示名（role.role_name_jp） */
  roleLabel?: string;
  /** スコープ（role.scope の雛形 or ユーザー編集後の値） */
  scope?: string;
}

/** 詳細フォームで使う対価行（フォーム編集中はすべて文字列で扱う） */
export interface OfferCompensationLine {
  label: string;
  unitPrice: string;
  quantity: string;
  /** 数量の単位（"式" / "本" / "時間" / "ヶ月" 等）。空文字許容。 */
  unit: string;
}

/** 詳細フォーム（雛形プリフィル後にユーザーが編集する） */
export interface OfferFormState {
  /**
   * ロール名（フォーム上で自由編集可能なテキスト）。
   * プリフィル時は `role.role_name_jp` で初期化されるが、その後の編集は
   * バックエンドのロールマスタとは紐付かない（テキスト送信のみ）。
   */
  roleName?: string;
  /** 納品形式 */
  deliverableFormat?: string;
  /**
   * 成果物提出有無（旧: `deliverableFormat` の有無からヒューリスティックに導出していた値）。
   * 仕様書 §3-1-7 より `requires_deliverable` は thread レベルのフラグで状態遷移にも
   * 影響するため、フォーム上で明示的に持つ。
   */
  requiresDeliverable?: boolean;
  /** 再委託可（仕様により default: false） */
  allowSubcontracting: boolean;
  /**
   * 受諾時に受託者へ付与するポジション（subcontract のみ）。
   * 仕様書 §3-1-7 / メンバーシップポジション API 表記統一に従い、UPPERCASE 3 値で管理する。
   * `null` は「変更なし」（PATCH 時はサーバ側で空文字列と等価扱い）。
   */
  acceptPosition?: "PRODUCER" | "DIRECTOR" | "SPECIALIST" | null;
  /** 着手金率（0–100）。PATCH 時は `advance_payment_rate` として送る */
  advancePaymentRate?: number;
  /**
   * 契約・支払いをプラットフォーム上で管理するスレッドか（既定 `true`）。
   * `false` のときはコミュニケーション専用スレッドとなり、契約締結ワークフローおよび
   * `PaymentRequest` ドメインを使用しない。本 PR では UI トグルを公開しないため
   * 通常はサーバ側 default に任せる（`undefined` のまま）。
   */
  isTransactional?: boolean;
  /** 納期（実日付。プリフィル時は「今日 + offset_days」+ 時刻で初期化） */
  deadline?: string; // ISO8601
  /**
   * 対価行（フラット）。配列順がそのまま表示順。
   *
   * BE 仕様 (2026-05-09 追加) で `offer_form.compensation_lines` が永続化対象になった。
   * non-empty で PATCH すると BE 側で `Σ unit_price * quantity` を計算して
   * `thread.contract_amount` に自動同期するため、FE は `compensation_lines` を送れば
   * `contract_amount` は明示送信不要。
   */
  compensationLines?: OfferCompensationLine[];
  /** 数量（offer_form.quantity / OfferCreator.quantity） */
  quantity?: string;
  /** 特記事項（テキストエリアで自由記述） */
  specialProvisionsText?: string;
  /** スコープ詳細 */
  scope?: string;
  /** メッセージ本文（雛形 = role.initial_message） */
  message?: string;
  /** 詳細アコーディオン側の値 */
  details?: {
    /** 有効期限（実日付） */
    validUntil?: string; // ISO8601
    /** 契約期間（実日付） */
    contractStartDate?: string | null; // ISO8601
    contractEndDate?: string | null; // ISO8601
    /** 取引方法 / 引渡場所 */
    deliveryMethod?: string;
    deliveryLocation?: string;
  };
  /**
   * master_contract 用の offer_form（type_only 対応）。
   * subcontract のドラフトでは未使用。本 PR では入力 UI を公開しないが、
   * `getDmThreadDetail` ハイドレートでこのスロットに値が入り、
   * 表示・PATCH 透過保存に使う。
   */
  masterOfferForm?: {
    proposedBudget?: number | null;
    contractType?: string;
    ownershipType?: "provide_from_us" | "provide_from_client";
    disclosureRule?: "can_public" | "discuss_later";
  };
}

/** 1スレッド分のウィザード状態 */
export interface OfferWizardData {
  recipient: OfferRecipientState;
  /** ロール選択ステップで選ばれた Role ID */
  roleId?: number;
  form: OfferFormState;
  /** スレッド一覧表示用のサマリ */
  summary: OfferDraftSummary;
}

/**
 * 詳細フォームのプリフィル元。
 * - `{ kind: "template" }`: ロールマスタの雛形（offer-prefill API）。既定。
 * - `{ kind: "history", dmId }`: 同じ相手 × 同じロール名で過去に送信したオファー。
 *
 * カード選択で切り替えると内容系フィールドが選択元の値で上書きされる
 * （日付系は常にテンプレートの相対計算を使う）。ステップ往復や再オープンで
 * ハイライトを維持するため entry に保持するが、サーバーには送らない。
 */
export type OfferPrefillSource =
  | { kind: "template" }
  | { kind: "history"; dmId: string };

/**
 * BE 側で永続化した DmThread (status: "new") との同期状況。
 *
 * - `idle`: 受信者未選択。まだサーバ側にドラフトレコードはない。
 * - `creating`: `POST /dm/` 実行中。
 * - `saving`: `PATCH /dm/<id>` 実行中（debounce で発火）。
 * - `synced`: 直近の差分が反映済み。
 * - `error`: 直近の API 呼び出しが失敗。
 */
export type OfferDraftSyncStatus =
  | "idle"
  | "creating"
  | "saving"
  | "synced"
  | "error";

/** Redux スライスに保持される、スレッド単位のウィザード状態 */
export interface OfferWizardEntry {
  isOpen: boolean;
  currentStep: OfferStep;
  data: OfferWizardData;
  /** ステップナビゲーションの並び順（①と②の入れ替え状態） */
  stepOrder: StepOrder;
  /** どのプロジェクトに紐付くドラフトか（ThreadList でフィルタするのに使う） */
  projectId: string;
  /** 作成タイムスタンプ（ドラフトの並び順用） */
  createdAt: string;
  /**
   * BE 側で作成済みの DmThread の ID。受信者選択直後に `POST /dm/` を成功させた
   * 時点で値が入る。それ以前はローカルのみのドラフト（`undefined`）。
   */
  serverDmId?: string;
  /** 詳細フォームのプリフィル元。未設定はテンプレート扱い。 */
  prefillSource?: OfferPrefillSource;
  /** サーバー同期の現在の状態（保存中インジケータ表示などに使う） */
  syncStatus: OfferDraftSyncStatus;
  /** 最後にサーバー側へ反映した時刻（ISO 8601） */
  lastSyncedAt?: string;
}

/** ウィザード起動時の初期 entry を生成 */
export const createInitialOfferWizardEntry = (
  projectId: string,
): OfferWizardEntry => ({
  isOpen: true,
  currentStep: "recipient",
  data: {
    recipient: { mode: "follows" },
    form: {
      allowSubcontracting: false,
    },
    summary: {},
  },
  stepOrder: "recipient_first",
  projectId,
  createdAt: new Date().toISOString(),
  syncStatus: "idle",
});
